import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import "../Sales Person/CreateSalesPerson.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

const SalesOutletData = ({
  selectedProductProp,
  selectedOutletChartProp,
  outletChartTypeProp,
  onChartDataChange,
}) => {
  const { api, Products = [], adminId } = useContext(GlobalContext);

  const sessionAdminId = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("userData");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.adminMeta?.adminId ? String(parsed.adminMeta.adminId) : "";
    } catch {
      return "";
    }
  }, []);

  const effectiveAdminId = adminId || sessionAdminId;

  const [outletMap, setOutletMap] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(
    selectedProductProp || ""
  );
  const [outletChartType, setOutletChartType] = useState(
    outletChartTypeProp || "Order"
  );
  const [selectedOutletChart, setSelectedOutletChart] = useState(
    selectedOutletChartProp || ""
  );
  const [outletTypeOptions, setOutletTypeOptions] = useState([]);
  const [outletList, setOutletList] = useState([]);
  const [loadingOutletChart, setLoadingOutletChart] = useState(false);
  const [outletChartData, setOutletChartData] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getTodayDate = () => new Date().toISOString().split("T")[0];
  const [outletChartFromDate, setOutletChartFromDate] = useState(
    getTodayDate()
  );
  const [outletChartToDate, setOutletChartToDate] = useState(getTodayDate());

  // Refs to prevent duplicate calls
  const abortControllerRef = useRef(null);
  const fetchTimeoutRef = useRef(null);

  const GetSalesOutletChartData = useCallback(
    async (
      adminIdParam,
      type = "Order",
      productId = "",
      from = "",
      to = "",
      selectedOutletName = ""
    ) => {
      if (!adminIdParam || !productId) return;

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoadingOutletChart(true);
      try {
        const outletIdToSend = outletMap[selectedOutletName] || "";

        const payload = {
          adminId: String(adminIdParam),
          productId: String(productId),
          outletId: String(outletIdToSend),
          type,
          fromDate: from || "",
          toDate: to || "",
        };

        const res = await api.post("Admin/v1.0/getSalesOutletData", payload, {
          signal: abortControllerRef.current.signal,
        });

        if (res?.data?.responseCode === "00") {
          if (Array.isArray(res.data.availableTypes)) {
            setOutletTypeOptions(res.data.availableTypes);
          }

          const transformedData = (res.data.data || []).map((item) => {
            let dateStr = item.date?.split(" ")[0];
            const dateObj = new Date(
              dateStr.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")
            );
            const formattedDate = dateObj.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            });
            return {
              name: formattedDate,
              value: parseFloat(item.totalValue || item.value) || 0,
            };
          });

          setOutletChartData(transformedData);
          onChartDataChange?.(transformedData);
        } else {
          setOutletChartData([]);
        }
      } catch (err) {
        if (err.name !== "CanceledError" && err.name !== "AbortError") {
          console.error("Outlet Chart API call failed:", err);
          setOutletChartData([]);
        }
      } finally {
        setLoadingOutletChart(false);
      }
    },
    [api, outletMap, onChartDataChange]
  );

  const fetchAllOutlets = useCallback(async () => {
    if (!effectiveAdminId || !selectedProduct) return;

    try {
      const res = await api.post("/Admin/v1.0/getOutletDetails", {
        adminId: String(effectiveAdminId),
        productId: String(selectedProduct),
      });

      if (res?.data?.responseCode === "00" && Array.isArray(res.data.data)) {
        const map = {};
        res.data.data.forEach((o) => (map[o.outlet_name] = o.pk_outlet_id));
        setOutletMap(map);
        setOutletList(res.data.data.map((o) => o.outlet_name));
      }
    } catch (err) {
      console.error("Failed to fetch outlets", err);
    }
  }, [api, effectiveAdminId, selectedProduct]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".aoc-csp-control-new")) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    fetchAllOutlets();
  }, [fetchAllOutlets]);

  // Set default outlet
  useEffect(() => {
    if (outletList.length > 0 && !selectedOutletChart) {
      setSelectedOutletChart(outletList[0]);
    }
  }, [outletList, selectedOutletChart]);

  useEffect(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    const shouldFetch =
      effectiveAdminId && selectedProduct && outletList.length > 0;

    if (shouldFetch) {
      fetchTimeoutRef.current = setTimeout(() => {
        GetSalesOutletChartData(
          effectiveAdminId,
          outletChartType,
          selectedProduct,
          outletChartFromDate,
          outletChartToDate,
          selectedOutletChart
        );
      }, 300);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [
    effectiveAdminId,
    selectedProduct,
    outletChartType,
    outletChartFromDate,
    outletChartToDate,
    selectedOutletChart,
    outletList.length,
    GetSalesOutletChartData,
  ]);

  // Sync with prop changes
  useEffect(() => {
    if (selectedProductProp) setSelectedProduct(selectedProductProp);
  }, [selectedProductProp]);

  return (
    <div className="bg-white rounded-lg shadow p-6 dark:bg-gray-800 transition-colors">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Sales Outlet Data
        </h2>
        <div className="aoc-csp-control-new relative">
          <div
            className="rounded px-3 py-1 text-sm cursor-pointer flex items-center justify-between min-w-[150px]"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span className="truncate">
              {selectedOutletChart || "All Outlets"}
            </span>
            <svg
              className={`w-4 h-4 ml-2 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {isDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-blue-300 rounded shadow-lg overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: "300px" }}>
                <div
                  className={`px-3 py-2 text-sm hover:bg-blue-100 cursor-pointer ${
                    selectedOutletChart === "" ? "bg-blue-200" : ""
                  }`}
                  onClick={() => {
                    setSelectedOutletChart("");
                    setIsDropdownOpen(false);
                  }}
                >
                  All Outlets
                </div>
                {outletList.map((name, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-2 text-sm hover:bg-blue-100 cursor-pointer ${
                      selectedOutletChart === name ? "bg-blue-200" : ""
                    }`}
                    onClick={() => {
                      setSelectedOutletChart(name);
                      setIsDropdownOpen(false);
                    }}
                  >
                    {name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <div className="aoc-csp-control-new">
          <select
            className="rounded px-3 py-1 text-sm aoc-csp-input"
            value={outletChartType}
            onChange={(e) => setOutletChartType(e.target.value)}
          >
            {(outletTypeOptions.length > 0
              ? outletTypeOptions
              : ["Order", "POS", "Campaign"]
            ).map((type, idx) => (
              <option key={idx} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-4 mb-4 justify-end">
          <input
            type="date"
            value={outletChartFromDate}
            onChange={(e) => setOutletChartFromDate(e.target.value)}
            className="custom-date-input text-sm"
          />
          <input
            type="date"
            value={outletChartToDate}
            onChange={(e) => setOutletChartToDate(e.target.value)}
            className="custom-date-input text-sm"
          />
        </div>
      </div>

      {loadingOutletChart ? (
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : outletChartData.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-gray-500">
          No data available
        </div>
      ) : (
        <div className="h-80 pt-5">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={outletChartData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, angle: -45, textAnchor: "end" }}
                height={80}
                interval={0}
                minTickGap={0}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value) => {
                  const numericValue = Number(value) || 0;
                  return numericValue.toLocaleString();
                }}
                labelFormatter={() => ""}
                contentStyle={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                cursor={false}
              />
              <Bar
                dataKey="value"
                fill="#10B981"
                radius={[4, 4, 0, 0]}
                barSize={outletChartData.length > 20 ? 30 : 45}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default SalesOutletData;
